import React, { useState, useEffect, useContext } from 'react';
import { EventSelectedRegistrationQuestion } from '../types/RegistrationQuestion';
import { EventInstrument } from '../types/Instrument';
import { getEventInstruments } from '../services/eventInstrumentService';
import { EventContext } from '../context/EventContext';

// Define the types based on your `eventSelectedRegistrationQuestion`
interface RegistrationQuestionComponentProps {
  question: EventSelectedRegistrationQuestion;
  onAnswer: (id: string, value: string | number | File) => void;
  answer?: string | number | File;
  event_id: string | null;
}

export const RegistrationQuestionComponent: React.FC<RegistrationQuestionComponentProps> = ({
  question,
  onAnswer,
  answer = '',
  event_id,
}) => {
  const [userAnswer, setUserAnswer] = useState<string | number | File>(answer); // Answer state
  const [showDescription, setShowDescription] = useState<boolean>(false); // Show description state
  const genders = ['Male', 'Female', 'Non-binary', 'Other'];
  const yesNoMaybe = ['Yes', 'No', 'Maybe'];
  const [instruments, setInstruments] = useState<EventInstrument[]>([]);

  const audio = 'audio/*';
  const video = 'video/*';
  const image = 'image/*';
  const document = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,';


  useEffect(() => {
    setUserAnswer(answer);
  }, [answer]);

  useEffect(() => {
    const fetchInstruments = async () => {
      if (event_id) {
        const eventInstruments = await getEventInstruments(event_id);
        setInstruments(eventInstruments);
      }
    };
    fetchInstruments();
  }, [event_id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { type, value } = e.target;
    if (type === 'file') {
      // Ensure that e.target is an HTMLInputElement before accessing 'files'
      const inputElement = e.target as HTMLInputElement;
      if (inputElement.files) {
        const file = inputElement.files[0];  // Get the first file
        const maxSizeInBytes = 2.0 * 1024 * 1024 * 1024; // 2.0GB
        if (file.size > maxSizeInBytes) {
          alert("File size must be under 2GB");
          e.target.value = "";
          return;
        }
        setUserAnswer(file);  // Store the file in state (or process as needed)
        onAnswer(question.event_selected_registration_question_id, file); // Send file to parent
      }
    } else {
      // Handle regular inputs (text, select, etc.)
      setUserAnswer(value);  // Store the string value
      onAnswer(question.event_selected_registration_question_id, value); // Send answer to parent
    }
  };

  const renderInputField = () => {
    switch (question.preset_registration_question_id) {
      case 'GENDER':
        return (
          <select value={typeof userAnswer === 'string' ? userAnswer : ''} onChange={handleChange} required={question.is_required} style={{ width: '100%' }}>
            <option value="">Select Gender</option> {/* Default placeholder option */}
            {genders.map((gender, index) => (
              <option key={index} value={gender}>
                {gender}
              </option>
            ))}
          </select>
        );
      case 'INSTRUMENT':
        return (
          <select value={typeof userAnswer === 'string' ? userAnswer : ''} onChange={handleChange} required={question.is_required} style={{ width: '100%' }}>
            <option value="">Select Instrument</option> {/* Default placeholder option */}
            {instruments.map((instrument, index) => (
              <option key={index} value={instrument.instrument_id}>
                {instrument.instrument_name}
              </option>
            ))}
          </select>
        );
      default:
        switch (question.question_type) {
          case 'text':
            return (
              <input
                type="text"
                value={(typeof userAnswer === 'string') ? String(userAnswer) : ''}
                onChange={handleChange}
                required={question.is_required}
                placeholder={question.is_required ? 'Required' : 'Optional'}
                minLength={question.min_value}
                maxLength={question.max_value}
                style={{ width: '100%' }}
              />
            );
          case 'number':
            return (
              <input
                type="number"
                value={
                  typeof answer === 'number' || (typeof answer === 'string' && answer !== '')
                    ? answer
                    : ''
                } onChange={handleChange}
                required={question.is_required}
                min={question.min_value}
                max={question.max_value}
                style={{ width: '100%' }}
              />
            );
          case 'email':
            return (
              <input
                type="email"
                value={typeof userAnswer === 'string' ? userAnswer : ''}
                onChange={handleChange}
                required={question.is_required}
                placeholder={question.is_required ? 'Required' : 'Optional'}
                minLength={question.min_value}
                maxLength={question.max_value}
                style={{ width: '100%' }}
              />
            );

          case 'checkbox':
            return (
              <input
                type="checkbox"
                checked={userAnswer === 'Yes'}
                onChange={(e) => {
                  const customEvent = {
                    ...e,
                    target: {
                      ...e.target,
                      value: e.target.checked ? 'Yes' : 'No',
                    },
                  };
                  handleChange(customEvent);
                }}
                required={question.is_required}
                style={{ transform: 'scale(1.2)' }}
              />
            );
          case 'radio':
            return (
              <div>
                {/* Radio buttons could be implemented here */}
              </div>
            );
          case 'file':
            const isAlreadyUploaded = typeof answer === 'string' && answer !== '';
            return (
              <div>
                {isAlreadyUploaded ? (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ color: 'green', margin: 0 }}>
                      Document already uploaded: <strong>{answer}</strong>
                    </p>
                    <label style={{ fontStyle: 'italic', color: 'gray' }}>
                      You cannot update media files once submitted.
                    </label>
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      onChange={handleChange}
                      required={question.is_required}
                      style={{ width: '100%' }}
                      accept={question.preset_registration_question_id === 'AUDIO' ? audio : question.preset_registration_question_id === 'VIDEO' ? video : question.preset_registration_question_id === 'IMAGE' ? image : document}
                    />
                    <label><i>Media files can not be updated once submitted. Organization and Host admin may be able to view file names.</i></label>
                  </div>
                )}
              </div>
            );
          case 'yesnomaybe':
            return (
              <select value={typeof userAnswer === 'string' ? userAnswer : ''} onChange={handleChange} required={question.is_required} style={{ width: '100%' }}>
                <option value="">Select</option>
                {yesNoMaybe.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            );
          default:
            return null;
        }
    }
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <label>
            <strong>{question.question_text}</strong>
            {(question.is_required == true) && <span style={{ color: 'red' }}> *</span>}
          </label>
          {(question.description && question.description != "") &&<button
            type="button"
            onClick={() => setShowDescription((prev) => !prev)}
            style={{
              background: 'none',
              cursor: 'pointer',
              padding: '5px',
              boxShadow: 'none',
              marginBottom: '10px',
              outline: 'none'
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#D6DfD8" viewBox="0 0 16 16">
              <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zM7.002 4a1 1 0 1 1 2 0 1 1 0 0 1-2 0zm.998 3a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-1 0v-4A.5.5 0 0 1 8 7z" />
            </svg>
          </button>}
        </div>
        {/* Input field along with info button and description */}
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <div style={{ width: '100%', maxWidth: '500px' }}>{renderInputField()}</div>

          {/* Show Info button and description */}
          {question.description && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {showDescription && <p style={{ marginLeft: '10px', display: 'inline-block' }}>{question.description}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
